import { BACKEND_URL } from "@/lib/api";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${BACKEND_URL}/api`;

export default function VerifyEmail() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    axios
      .post(`${API}/auth/verify-email/${token}`)
      .then((r) => setState({ loading: false, ok: true, email: r.data.email, already: r.data.already_verified }))
      .catch((e) => setState({
        loading: false,
        ok: false,
        error: e?.response?.data?.detail || "Verification link is invalid or has expired.",
      }));
  }, [token]);

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative bg-[#0F172A] text-white p-12 flex-col">
        <Link to="/" className="inline-flex items-center gap-2.5">
          <span className="inline-grid place-items-center h-8 w-8 rounded-md bg-white">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="#0F172A" strokeWidth="2">
              <path d="M4 7h7M4 12h12M4 17h7" strokeLinecap="round" /><circle cx="18" cy="7" r="2.2" fill="#0055FF" stroke="none" /><circle cx="18" cy="17" r="2.2" fill="#0055FF" stroke="none" />
            </svg>
          </span>
          <span className="font-semibold tracking-tight">Connexis<span className="text-[#0055FF]">.</span></span>
        </Link>
        <div className="mt-auto max-w-md">
          <p className="text-lg tracking-tight leading-snug">Verified accounts get invoices, payout receipts and alerts straight to their inbox.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-md text-center" data-testid="verify-email-page">
          {state.loading ? (
            <>
              <span className="inline-grid place-items-center size-14 rounded-full bg-slate-50 text-slate-400 mx-auto">
                <Loader2 className="size-7 animate-spin" />
              </span>
              <h1 className="mt-5 text-2xl tracking-[-0.02em] font-semibold text-slate-900">Verifying your email…</h1>
            </>
          ) : state.ok ? (
            <>
              <span className="inline-grid place-items-center size-14 rounded-full bg-emerald-50 text-emerald-600 mx-auto">
                <CheckCircle2 className="size-7" strokeWidth={1.6} />
              </span>
              <h1 className="mt-5 text-3xl tracking-[-0.02em] font-semibold text-slate-900" data-testid="verify-email-ok">
                {state.already ? "Already verified" : "Email verified"}
              </h1>
              <p className="mt-2.5 text-sm text-slate-600">
                {state.email && <><b>{state.email}</b> is confirmed.</>} You'll now receive invoices, payout receipts and account alerts at this address.
              </p>
              <Button asChild className="mt-7 rounded-full" data-testid="verify-email-continue">
                <Link to="/login">Continue to sign in →</Link>
              </Button>
            </>
          ) : (
            <>
              <span className="inline-grid place-items-center size-14 rounded-full bg-rose-50 text-rose-600 mx-auto">
                <AlertCircle className="size-7" strokeWidth={1.6} />
              </span>
              <h1 className="mt-5 text-3xl tracking-[-0.02em] font-semibold text-slate-900" data-testid="verify-email-fail">Couldn't verify</h1>
              <p className="mt-2.5 text-sm text-slate-600">{state.error}</p>
              <p className="mt-2 text-sm text-slate-500">Sign in and click "Send verification email" on your account page to get a fresh link.</p>
              <Button asChild className="mt-7 rounded-full" variant="outline">
                <Link to="/login">Go to sign in</Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
